import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { 
  Shield, FileText, BarChart3, Clock, Users, CheckCircle2, 
  AlertTriangle, Building2, Scale, Eye, Lock, Zap, ArrowRight,
  ChevronRight, Target, Layers, FileCheck, Brain, History,
  TrendingUp, Award, Phone, Mail, MapPin, Send, Play, Sparkles, LogIn, Flag,
  Hash, ShieldCheck, FileSignature, QrCode, UserCheck, Wand2, ShieldAlert, Database
} from 'lucide-react';
import { toast } from 'sonner';

const LandingPage = () => {
  const { api } = useAuth();
  const [formData, setFormData] = useState({
    name: '', organization: '', role: '', email: '', phone: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [animatedStats, setAnimatedStats] = useState({ stat1: 0, stat2: 0, stat3: 0 });

  // Animate stats on load
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    let current = { stat1: 0, stat2: 0, stat3: 0 };
    
    const timer = setInterval(() => {
      current = {
        stat1: Math.min(current.stat1 + 100 / steps, 100),
        stat2: Math.min(current.stat2 + 70 / steps, 70),
        stat3: Math.min(current.stat3 + 3 / steps, 3),
      };
      setAnimatedStats({ ...current });
      
      if (current.stat1 >= 100) clearInterval(timer);
    }, interval);
    
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Mensagem enviada! Nossa equipe entrará em contato em breve.');
        setFormData({ name: '', organization: '', role: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const painPoints = [
    { icon: FileText, text: 'Acompanhamento manual via e-mail, planilhas e malotes físicos' },
    { icon: Eye, text: 'Ausência de visão centralizada dos atos de controle' },
    { icon: Layers, text: 'Baixa padronização nas respostas aos órgãos fiscalizadores' },
    { icon: FileCheck, text: 'Dificuldade em gerar e organizar evidências comprobatórias' },
    { icon: AlertTriangle, text: 'Exposição de ordenadores de despesa e secretários' },
    { icon: History, text: 'Perda de memória institucional entre gestões' },
  ];

  const features = [
    { icon: Shield, title: 'IPI — Índice de Proteção Institucional', desc: 'Score único 0–100 que mede a maturidade de conformidade do tenant em tempo real.' },
    { icon: Eye, title: 'Visão Executiva', desc: 'Snapshot consolidado para o gabinete: IPI, atos críticos, vencimentos e ranking de unidades por risco.' },
    { icon: Wand2, title: 'Wizard Inteligente de Criação de Atos', desc: '8 passos guiados com autosave, validação por etapa e sugestão de classificação por IA.' },
    { icon: FileSignature, title: 'Dossiê Executivo Verificável', desc: 'PDF institucional com QR Code, hash SHA-256 e URL pública de verificação para qualquer terceiro.' },
    { icon: QrCode, title: 'Verificação Pública de Dossiês', desc: 'Página pública sem login para TCE, CGU, MP ou imprensa conferirem autenticidade.' },
    { icon: UserCheck, title: 'Auditor Externo', desc: 'Convite temporário com PIN de 6 dígitos e sessão de 8 horas, somente leitura, registrada em auditoria.' },
    { icon: Hash, title: 'Hash Chain de Auditoria', desc: 'Trilha de eventos encadeada por SHA-256 com endpoint público de verificação de integridade.' },
    { icon: Brain, title: 'Inteligência de Risco', desc: 'Score por ato (0–100), classificação, fatores ponderados, recorrência 12 m e resumo executivo.' },
    { icon: History, title: 'Memória Institucional', desc: 'Histórico permanente, evidências preservadas e continuidade garantida entre gestões.' },
  ];

  const benefits = [
    { icon: Shield, text: 'Redução significativa do risco institucional' },
    { icon: TrendingUp, text: 'Aumento de governança e previsibilidade' },
    { icon: Users, text: 'Proteção efetiva de gestores e ordenadores' },
    { icon: Zap, text: 'Velocidade 3x maior nas respostas' },
    { icon: CheckCircle2, text: 'Eliminação de retrabalho e reincidências' },
    { icon: FileCheck, text: 'Evidências organizadas para qualquer auditoria' },
    { icon: Award, text: 'Transição de gestão sem perda de informação' },
  ];

  return (
    <div className="min-h-screen bg-white antialiased">
      {/* Premium Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100/50">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/bluegov-logo.png" 
              alt="BlueGov" 
              className="w-11 h-11 rounded-2xl shadow-lg shadow-blue-500/25 object-contain"
            />
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">BlueGov</span>
            </div>
          </div>
          <div className="hidden lg:flex items-center gap-8">
            <a href="#por-que-bluegov" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Por que BlueGov</a>
            <a href="#fluxo" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Como funciona</a>
            <a href="#para-quem" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Para quem</a>
            <a href="#plataforma" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Plataforma</a>
            <a href="#faq" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">FAQ</a>
            <a href="#contato" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Contato</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button className="hidden md:flex bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-blue-700 shadow-lg shadow-blue-500/25 font-semibold">
                <LogIn className="w-4 h-4 mr-2" />
                Entrar
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section - Premium with Background Image */}
      <section className="pt-32 pb-24 px-8 relative overflow-hidden min-h-[90vh] flex items-center">
        {/* Hero Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(/hero-landing.jpg)' }}
        />
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-transparent" />
        
        {/* Subtle animated particles effect */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-blue-400/30 rounded-full animate-pulse" />
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-indigo-400/40 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-cyan-400/30 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8">
              <Shield className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-blue-200">Camada de Controle, Conformidade e Proteção Institucional</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] tracking-tight mb-8" data-testid="landing-hero-title">
              Sua gestão já tem sistemas.{' '}
              <span className="relative">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-indigo-400">
                  Mas tem controle?
                </span>
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 leading-relaxed mb-12 max-w-2xl font-light" data-testid="landing-hero-subtitle">
              Nenhum sistema atual controla de forma integrada
              <span className="font-semibold text-white"> recomendações</span>,{' '}
              <span className="font-semibold text-white">apontamentos</span> e{' '}
              <span className="font-semibold text-white">ações corretivas</span>.
              O BlueGov centraliza responsabilidades, acompanha providências, monitora prazos e demonstra o cumprimento das ações de controle e conformidade institucional.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-16">
              <Button 
                size="lg" 
                data-testid="landing-cta-demo"
                className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-base h-14 px-10 shadow-xl shadow-blue-500/30 font-semibold rounded-xl border border-blue-400/30"
                onClick={() => document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Solicitar Demonstração
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                data-testid="landing-cta-platform"
                className="text-base h-14 px-10 border-2 border-white/30 bg-white/5 backdrop-blur-sm text-white hover:bg-white/10 hover:border-white/50 font-semibold rounded-xl"
                onClick={() => document.getElementById('por-que-bluegov')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Eye className="w-5 h-5 mr-2" />
                Entender em 30 segundos
              </Button>
            </div>
          </div>

          {/* Premium Stats - Glass morphism */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {[
              { value: `${Math.round(animatedStats.stat1)}%`, label: 'Rastreabilidade Total', sublabel: 'Auditável' },
              { value: 'SHA-256', label: 'Hash Chain de Auditoria', sublabel: 'Imutável' },
              { value: 'QR', label: 'Verificação Pública', sublabel: 'Dossiê Executivo' },
              { value: 'IPI', label: 'Índice de Proteção', sublabel: 'Institucional' },
            ].map((stat, i) => (
              <div key={i} className="text-center p-6 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/15 transition-all duration-500">
                <p className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</p>
                <p className="text-base font-semibold text-white/90">{stat.label}</p>
                <p className="text-xs text-blue-300/80 uppercase tracking-wider mt-1">{stat.sublabel}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== NOVA SEÇÃO 1 — POR QUE BLUEGOV ============== */}
      <section id="por-que-bluegov" className="py-24 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <Badge className="mb-6 px-4 py-1.5 bg-blue-50 text-blue-700 border-blue-100 font-semibold">
              Posicionamento
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Você já possui sistemas.{' '}
              <span className="text-blue-700">Mas possui controle?</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed font-light">
              O BlueGov não substitui o que seu órgão já tem. Ele preenche a lacuna que nenhum sistema atual cobre:
              o controle do <strong className="text-slate-900">cumprimento</strong> das ações decorrentes desses sistemas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="comparison-grid">
            {[
              { name: 'SEI', desc: 'Tramita processos.', miss: 'Não acompanha a execução das correções.', icon: FileText },
              { name: 'ERP', desc: 'Controla recursos.', miss: 'Não controla ações corretivas.', icon: BarChart3 },
              { name: 'Patrimônio', desc: 'Controla bens.', miss: 'Não controla cumprimento de recomendações.', icon: Building2 },
              { name: 'Contratos', desc: 'Controla contratos.', miss: 'Não controla planos de ação.', icon: FileCheck },
              { name: 'Portal da Transparência', desc: 'Publica informações.', miss: 'Não controla atendimento a apontamentos.', icon: Eye },
              { name: 'Controle Interno', desc: 'Identifica achados.', miss: 'Não dispõe de plataforma para acompanhar correções.', icon: Scale },
            ].map((s, i) => (
              <div key={i} className="p-6 rounded-2xl border border-slate-200 bg-slate-50/50 hover:bg-white hover:border-slate-300 transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-slate-200/70 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{s.name}</h3>
                </div>
                <p className="text-sm text-slate-700 mb-3 leading-relaxed">{s.desc}</p>
                <p className="text-sm text-red-600 font-semibold leading-relaxed">{s.miss}</p>
              </div>
            ))}

            {/* Card destaque BlueGov */}
            <div className="md:col-span-2 lg:col-span-3 p-8 rounded-2xl bg-gradient-to-br from-blue-700 to-blue-900 text-white shadow-2xl shadow-blue-900/30">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold">BlueGov</h3>
                <Badge className="ml-2 bg-white/15 text-white border-white/20 font-semibold">Camada de Controle</Badge>
              </div>
              <p className="text-base text-blue-100 mb-5 leading-relaxed">
                A única plataforma do seu órgão dedicada a transformar apontamentos em ações controladas e auditáveis.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  'Centraliza apontamentos',
                  'Define responsáveis',
                  'Controla prazos',
                  'Recebe evidências',
                  'Mede conformidade',
                  'Gera dossiês',
                  'Demonstra resultados',
                  'Protege a gestão',
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm font-medium">
                    <CheckCircle2 className="w-4 h-4 text-cyan-300 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== SEÇÃO — O CUSTO INVISÍVEL DA FALTA DE CONTROLE ============== */}
      <section id="riscos" className="py-24 px-8 bg-gradient-to-b from-white via-slate-50/40 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <Badge className="mb-6 px-4 py-1.5 bg-slate-100 text-slate-700 border-slate-200 font-semibold">
              Olhar Institucional
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              O custo invisível da{' '}
              <span className="text-slate-600">falta de controle</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed font-light">
              Muitas organizações possuem sistemas, auditorias e processos. O desafio está em garantir
              que cada apontamento seja acompanhado até sua efetiva solução e possa ser demonstrado futuramente.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="riscos-grid">
            {[
              {
                title: 'Reincidência de apontamentos',
                desc: 'Problemas já identificados voltam a aparecer em auditorias futuras por falta de acompanhamento estruturado.',
                icon: History,
              },
              {
                title: 'Dificuldade na prestação de contas',
                desc: 'Providências podem ter sido tomadas, mas sem evidências organizadas a comprovação se torna difícil.',
                icon: FileText,
              },
              {
                title: 'Perda de histórico institucional',
                desc: 'Informações ficam espalhadas entre e-mails, planilhas, documentos e pessoas.',
                icon: Layers,
              },
              {
                title: 'Exposição da gestão',
                desc: 'Quando não existe rastreabilidade das providências adotadas, a instituição encontra dificuldades para demonstrar que tratou adequadamente os apontamentos realizados pelos órgãos de controle.',
                icon: ShieldAlert,
              },
              {
                title: 'Baixa visibilidade executiva',
                desc: 'A alta gestão perde a capacidade de identificar rapidamente riscos, atrasos e pendências críticas.',
                icon: TrendingUp,
              },
              {
                title: 'Fragilidade na continuidade administrativa',
                desc: 'Mudanças de equipes e gestores podem comprometer o acompanhamento de ações iniciadas anteriormente.',
                icon: Users,
              },
              {
                title: 'Conhecimento perdido',
                desc: 'Mudanças de equipe, gestores ou fornecedores podem causar perda de histórico e dificultar a continuidade das ações corretivas já iniciadas.',
                icon: Database,
              },
            ].map((card, i) => (
              <div
                key={i}
                className="group p-7 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-500"
                data-testid={`risco-card-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-5 group-hover:bg-slate-200 transition-colors">
                  <card.icon className="w-6 h-6 text-slate-700" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3 leading-snug">{card.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>

          {/* Bloco de fechamento institucional */}
          <div className="mt-14 max-w-4xl mx-auto p-10 rounded-2xl bg-gradient-to-br from-blue-700 to-blue-900 text-white relative overflow-hidden" data-testid="bloco-fechamento-custo-invisivel">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-cyan-300 to-blue-400" />
            <p className="text-xl md:text-2xl text-white leading-relaxed font-light text-center">
              O BlueGov transforma <strong className="font-bold">apontamentos</strong>,{' '}
              <strong className="font-bold">recomendações</strong> e{' '}
              <strong className="font-bold">determinações</strong> em ações{' '}
              <strong className="font-bold">acompanhadas</strong>,{' '}
              <strong className="font-bold">rastreáveis</strong> e{' '}
              <strong className="font-bold">comprováveis</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* ============== NOVA SEÇÃO 2 — DO APONTAMENTO À SOLUÇÃO (FLUXO) ============== */}
      <section id="fluxo" className="py-24 px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Badge className="mb-6 px-4 py-1.5 bg-cyan-50 text-cyan-700 border-cyan-100 font-semibold">
              Como funciona
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Do apontamento à solução
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed font-light">
              Cada apontamento de auditoria percorre um caminho rastreável até a comprovação institucional.
            </p>
          </div>

          <div className="relative" data-testid="solution-flow">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { step: '01', title: 'Auditoria', desc: 'TCE, CGU, MPE, Controle Interno', icon: Scale },
                { step: '02', title: 'Apontamento', desc: 'Recomendação ou determinação', icon: AlertTriangle },
                { step: '03', title: 'Responsável', desc: 'Designado com clareza', icon: UserCheck },
                { step: '04', title: 'Plano de ação', desc: 'Etapas, prazos, marcos', icon: Target },
                { step: '05', title: 'Evidências', desc: 'Documentos com hash', icon: FileCheck },
                { step: '06', title: 'Validação', desc: 'Controle interno revisa', icon: ShieldCheck },
                { step: '07', title: 'Conformidade', desc: 'Status atualizado', icon: CheckCircle2 },
                { step: '08', title: 'Dossiê Executivo', desc: 'Prova auditável', icon: FileSignature },
              ].map((f, i) => (
                <div key={i} className="relative p-6 rounded-2xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all group">
                  <div className="absolute -top-3 left-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-bold px-2.5 py-1 rounded-md tracking-wider">
                    {f.step}
                  </div>
                  <div className="mt-3 mb-3 w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                    <f.icon className="w-5 h-5 text-blue-700" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-1">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-snug">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============== NOVA SEÇÃO 3 — PARA QUEM (SEM/COM ESTRUTURA) ============== */}
      <section id="para-quem" className="py-24 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <Badge className="mb-6 px-4 py-1.5 bg-indigo-50 text-indigo-700 border-indigo-100 font-semibold">
              Modelo de adoção
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Funciona em qualquer nível de maturidade institucional
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed font-light">
              Não importa se a instituição está iniciando sua estrutura de controle ou se já possui processos, auditorias e sistemas consolidados. O BlueGov se adapta aos dois cenários.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Para órgãos sem estrutura */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 hover:shadow-xl transition-all" data-testid="para-orgaos-sem-estrutura">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-emerald-700" />
                </div>
                <div>
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 font-semibold text-xs mb-1">Modelo Autônomo</Badge>
                  <h3 className="text-2xl font-bold text-slate-900">Para órgãos sem estrutura</h3>
                </div>
              </div>
              <p className="text-slate-700 mb-6 leading-relaxed">
                <strong>Implante em poucos dias.</strong> Sem integrações complexas. Toda a operação acontece dentro do BlueGov.
              </p>
              <div className="space-y-2.5">
                {['Atos de controle', 'Evidências', 'Planos de ação', 'Prazos', 'Responsáveis', 'Dossiês', 'Auditoria'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Para órgãos com estrutura */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-white border border-blue-200 hover:shadow-xl transition-all" data-testid="para-orgaos-com-estrutura">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Layers className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <Badge className="bg-blue-50 text-blue-700 border-blue-200 font-semibold text-xs mb-1">Modelo Integrado</Badge>
                  <h3 className="text-2xl font-bold text-slate-900">Para órgãos com estrutura</h3>
                </div>
              </div>
              <p className="text-slate-700 mb-6 leading-relaxed">
                Conecte o BlueGov ao ecossistema tecnológico já utilizado pela instituição, preservando investimentos, processos e sistemas existentes.
              </p>
              <div className="space-y-2.5">
                {[
                  'SEI',
                  'Microsoft Entra ID (Azure AD)',
                  'ICP-Brasil',
                  'Sistemas Corporativos',
                  'Webhooks',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== NOVA SEÇÃO 4 — PROTEÇÃO INSTITUCIONAL ============== */}
      <section className="py-24 px-8 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 relative overflow-hidden" data-testid="protecao-institucional">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:3rem_3rem]" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <Badge className="mb-6 px-4 py-1.5 bg-white/10 text-blue-200 border-white/20 font-semibold backdrop-blur-sm">
              Defesa Institucional
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Proteção Institucional
            </h2>
            <p className="text-lg text-blue-100/85 leading-relaxed font-light">
              O BlueGov não foi criado apenas para registrar problemas. Foi criado para <strong className="text-white">demonstrar que os problemas foram tratados</strong>.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { name: 'IPI', desc: 'Índice de Proteção Institucional', icon: Shield },
              { name: 'Visão Executiva', desc: 'Painel consolidado para a alta gestão', icon: TrendingUp },
              { name: 'Hash Chain', desc: 'Trilha criptográfica imutável (SHA-256)', icon: Hash },
              { name: 'Inteligência de Risco', desc: 'Detecção de recorrência e padrões', icon: Brain },
              { name: 'Dossiê Executivo', desc: 'Pacote auditável pronto para entrega', icon: FileSignature },
              { name: 'Auditor Externo', desc: 'Acesso controlado e temporário ao TCE, TCU, MPE', icon: UserCheck },
            ].map((p, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all">
                <div className="w-11 h-11 rounded-lg bg-blue-500/20 flex items-center justify-center mb-4 border border-blue-400/30">
                  <p.icon className="w-5 h-5 text-cyan-300" />
                </div>
                <h3 className="text-lg font-bold text-white mb-1.5">{p.name}</h3>
                <p className="text-sm text-blue-100/75 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== SEÇÃO REESCRITA — PILARES INSTITUCIONAIS ============== */}
      <section id="solucao" className="py-24 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Badge className="mb-6 px-4 py-1.5 bg-blue-50 text-blue-700 border-blue-100 font-semibold">
              Diferenciais Institucionais
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Por que o BlueGov é diferente
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed font-light">
              Cada recurso da plataforma foi desenhado em torno de um princípio: <strong className="text-slate-900">defender a gestão e a instituição</strong>.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pillars-grid">
            {[
              {
                icon: Shield,
                title: 'Proteção Institucional',
                desc: 'O BlueGov foi desenhado para defender o órgão e seus gestores, demonstrando que cada apontamento foi tratado com seriedade e rastreabilidade.',
              },
              {
                icon: CheckCircle2,
                title: 'Conformidade',
                desc: 'Acompanhamento contínuo do cumprimento de determinações, recomendações e obrigações legais — sem depender de planilhas paralelas.',
              },
              {
                icon: History,
                title: 'Rastreabilidade',
                desc: 'Hash chain criptográfica SHA-256 registra de forma imutável quem fez o quê, quando e por quê. Auditores externos verificam em segundos.',
              },
              {
                icon: UserCheck,
                title: 'Responsabilização',
                desc: 'Cada ato, prazo e evidência tem responsável formal designado. A cultura de "isso é problema de outro" deixa de existir.',
              },
              {
                icon: FileCheck,
                title: 'Evidências Estruturadas',
                desc: 'Documentos, fotos, planilhas e e-mails deixam de viver em pastas dispersas. Cada evidência ganha hash, autor, data e vínculo com o ato.',
              },
              {
                icon: FileSignature,
                title: 'Dossiê Executivo',
                desc: 'Em um clique, o BlueGov gera um PDF institucional com sumário, evidências, autoria e QR Code de verificação pública — pronto para entrega.',
              },
              {
                icon: Users,
                title: 'Auditor Externo',
                desc: 'TCE, TCU, CGU e consultorias recebem acesso temporário, escopado e somente-leitura. Cada ação fica registrada em logs separados.',
              },
              {
                icon: Layers,
                title: 'Integração com SEI',
                desc: 'Processos do SEI viram atos do BlueGov com idempotência por hash. Atualizações no SEI refletem automaticamente, sem duplicação.',
              },
              {
                icon: Lock,
                title: 'Microsoft Entra ID (Azure AD)',
                desc: 'SSO corporativo nativo. Os grupos do Azure mapeiam papéis do BlueGov. Sem senha duplicada. Desligamento no RH revoga acesso na hora.',
              },
            ].map((pillar, i) => (
              <div
                key={i}
                className="group p-7 rounded-2xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-xl hover:shadow-blue-50/60 transition-all duration-500"
                data-testid={`pillar-card-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <pillar.icon className="w-6 h-6 text-blue-700" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{pillar.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Visual Section - Premium Mockups */}
      <section id="plataforma" className="py-24 px-8 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Badge className="mb-6 px-4 py-1.5 bg-blue-50 text-blue-700 border-blue-100 font-semibold">Visão da Plataforma</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 leading-tight">
              Interface projetada para{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">alto escalão</span>
            </h2>
            <p className="text-xl text-slate-600 font-light">
              Dashboards executivos, gestão de atos, evidências e respostas — tudo em uma única plataforma.
            </p>
          </div>

          {/* Premium Mock Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Dashboard Card */}
            <Card className="overflow-hidden border-0 shadow-2xl shadow-slate-200/50 lg:col-span-2 rounded-3xl">
              <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-700 p-5">
                <p className="text-white font-bold text-lg">Dashboard Executivo</p>
                <p className="text-blue-200 text-sm">Visão estratégica em tempo real</p>
              </div>
              <CardContent className="p-8 bg-gradient-to-br from-slate-50 to-white">
                <div className="grid grid-cols-3 gap-6 mb-8">
                  {[
                    { label: 'Total de Atos', value: '847', color: 'from-blue-600 to-indigo-600' },
                    { label: 'Em Execução', value: '234', color: 'from-amber-500 to-orange-500' },
                    { label: 'Concluídos', value: '613', color: 'from-emerald-500 to-teal-500' },
                  ].map((item, i) => (
                    <div key={i} className="text-center p-5 rounded-2xl bg-white border border-slate-100 shadow-lg shadow-slate-100/50">
                      <p className={`text-3xl font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent`}>{item.value}</p>
                      <p className="text-sm text-slate-500 mt-2 font-medium">{item.label}</p>
                    </div>
                  ))}
                </div>
                <div className="h-32 rounded-2xl bg-gradient-to-r from-blue-50 via-indigo-50/50 to-blue-50 flex items-center justify-center border border-blue-100/50">
                  <BarChart3 className="w-16 h-16 text-blue-200" />
                </div>
              </CardContent>
            </Card>

            {/* Risk Card */}
            <Card className="overflow-hidden border-0 shadow-2xl shadow-slate-200/50 rounded-3xl">
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-5">
                <p className="text-white font-bold text-lg">Indicadores de Risco</p>
                <p className="text-slate-400 text-sm">Monitoramento contínuo</p>
              </div>
              <CardContent className="p-6 space-y-5">
                {[
                  { label: 'Críticos', value: 12, color: 'from-red-500 to-rose-500', percent: 15 },
                  { label: 'Atrasados', value: 28, color: 'from-amber-500 to-orange-500', percent: 35 },
                  { label: 'No Prazo', value: 194, color: 'from-emerald-500 to-teal-500', percent: 85 },
                ].map((item, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600 font-medium">{item.label}</span>
                      <span className="font-bold text-slate-900">{item.value}</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r ${item.color} rounded-full transition-all duration-1000`}
                        style={{ width: `${item.percent}%` }}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Score Card */}
            <Card className="overflow-hidden border-0 shadow-2xl shadow-slate-200/50 rounded-3xl lg:col-span-2">
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5">
                <p className="text-white font-bold text-lg">Score de Conformidade</p>
                <p className="text-emerald-200 text-sm">Avaliação em tempo real</p>
              </div>
              <CardContent className="p-8">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="relative w-32 h-32">
                        <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
                          <circle cx="50" cy="50" r="42" fill="none" stroke="#E2E8F0" strokeWidth="12" />
                          <circle 
                            cx="50" cy="50" r="42" fill="none" 
                            stroke="url(#scoreGradient2)" strokeWidth="12"
                            strokeLinecap="round"
                            strokeDasharray={`${85 * 2.64} ${100 * 2.64}`}
                          />
                          <defs>
                            <linearGradient id="scoreGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="#10B981" />
                              <stop offset="100%" stopColor="#14B8A6" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <p className="text-4xl font-bold text-slate-900">85%</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-slate-900 mb-2">Excelente</p>
                        <p className="text-slate-500">Nível de conformidade</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-6">
                    {[
                      { label: 'Documentados', value: '94%' },
                      { label: 'No Prazo', value: '87%' },
                      { label: 'Validados', value: '91%' },
                    ].map((item, i) => (
                      <div key={i} className="text-center p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                        <p className="text-2xl font-bold text-emerald-600">{item.value}</p>
                        <p className="text-xs text-slate-500 mt-1 font-medium">{item.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Timeline Card */}
            <Card className="overflow-hidden border-0 shadow-2xl shadow-slate-200/50 rounded-3xl">
              <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-5">
                <p className="text-white font-bold text-lg">Linha do Tempo</p>
                <p className="text-indigo-200 text-sm">Ciclo de vida do ato</p>
              </div>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {[
                    { status: 'Recebido', date: '12/01', done: true },
                    { status: 'Classificado', date: '13/01', done: true },
                    { status: 'Em Execução', date: '15/01', active: true },
                    { status: 'Resposta', date: '-', done: false },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className={`w-4 h-4 rounded-full flex-shrink-0 ${
                        item.active ? 'bg-gradient-to-r from-blue-500 to-indigo-500 ring-4 ring-blue-100' : 
                        item.done ? 'bg-emerald-500' : 'bg-slate-200'
                      }`} />
                      <div className="flex-1">
                        <p className={`font-semibold ${item.active ? 'text-blue-600' : 'text-slate-700'}`}>{item.status}</p>
                      </div>
                      <p className="text-sm text-slate-400 font-medium">{item.date}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Protection Section */}
      <section className="py-24 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <Badge className="mb-6 px-4 py-1.5 bg-blue-50 text-blue-700 border-blue-100 font-semibold">Proteção ao Gestor</Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 leading-tight">
                Segurança jurídica em{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">cada etapa</span>
              </h2>
              <p className="text-xl text-slate-600 mb-10 leading-relaxed font-light">
                O BlueGov previne sanções, atrasos e responsabilização de gestores, 
                garantindo evidência estruturada e resposta qualificada.
              </p>
              
              <div className="space-y-5">
                {[
                  'Suporte à fundamentação jurídica',
                  'Trilha de auditoria completa',
                  'Compliance público integrado',
                  'Histórico e documentação estruturada'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-lg text-slate-700 font-medium">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-[2rem] transform rotate-3" />
              <Card className="relative bg-white shadow-2xl shadow-blue-100/50 border-0 rounded-[2rem] overflow-hidden">
                <CardContent className="p-10">
                  <div className="flex items-center gap-5 mb-8">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 via-blue-700 to-blue-700 flex items-center justify-center shadow-xl shadow-blue-500/30">
                      <Shield className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">Proteção Ativa</p>
                      <p className="text-slate-500">Monitoramento contínuo</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { icon: Lock, label: 'Dados Protegidos', value: 'LGPD' },
                      { icon: QrCode, label: 'Verificação Pública', value: 'QR Code' },
                      { icon: Hash, label: 'Cadeia de Auditoria', value: 'SHA-256' },
                      { icon: UserCheck, label: 'Auditor Externo', value: 'PIN 8h' },
                    ].map((item, i) => (
                      <div key={i} className="p-5 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50/30 border border-slate-100">
                        <item.icon className="w-6 h-6 text-blue-600 mb-3" />
                        <p className="text-2xl font-bold text-slate-900">{item.value}</p>
                        <p className="text-sm text-slate-500">{item.label}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Quem Usa - Fluxo Institucional */}
      <section className="py-20 px-8 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Usado por quem realmente trabalha com{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Atos de Controle</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              O BlueGov reflete a cadeia real de responsabilização do setor público
            </p>
          </div>

          {/* Fluxo Institucional - 5 Blocos */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {/* Quem Coordena */}
            <div className="group p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50/50 border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">Quem coordena</p>
              <ul className="space-y-1">
                <li className="text-slate-700 font-medium text-sm">Casa Civil</li>
              </ul>
            </div>

            {/* Quem Controla */}
            <div className="group p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50/50 border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">Quem controla</p>
              <ul className="space-y-1">
                <li className="text-slate-700 font-medium text-sm">CGE</li>
                <li className="text-slate-500 text-sm">Controladorias Internas</li>
              </ul>
            </div>

            {/* Quem Defende */}
            <div className="group p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-amber-50/50 border border-slate-100 hover:border-amber-200 hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-2">Quem defende</p>
              <ul className="space-y-1">
                <li className="text-slate-700 font-medium text-sm">PGE</li>
                <li className="text-slate-500 text-sm">Procuradorias Municipais</li>
              </ul>
            </div>

            {/* Quem Executa */}
            <div className="group p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-emerald-50/50 border border-slate-100 hover:border-emerald-200 hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Layers className="w-6 h-6 text-white" />
              </div>
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">Quem executa</p>
              <ul className="space-y-1">
                <li className="text-slate-700 font-medium text-sm">Secretarias</li>
                <li className="text-slate-500 text-sm">Autarquias</li>
                <li className="text-slate-500 text-sm">Fundações</li>
              </ul>
            </div>

            {/* Quem Assina */}
            <div className="group p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-rose-50/50 border border-slate-100 hover:border-rose-200 hover:shadow-lg transition-all duration-300 col-span-2 md:col-span-1">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Flag className="w-6 h-6 text-white" />
              </div>
              <p className="text-xs font-semibold text-rose-600 uppercase tracking-wider mb-2">Quem assina</p>
              <ul className="space-y-1">
                <li className="text-slate-700 font-medium text-sm">Governos Estaduais</li>
                <li className="text-slate-500 text-sm">Governos Municipais</li>
              </ul>
            </div>
          </div>

          {/* Texto de Suporte */}
          <div className="mt-12 text-center">
            <p className="text-slate-500 max-w-3xl mx-auto leading-relaxed">
              O BlueGov conecta todas as unidades responsáveis por responder Atos de Controle provenientes de{' '}
              <span className="font-semibold text-slate-700">TCE</span>,{' '}
              <span className="font-semibold text-slate-700">Ministério Público de Contas</span>,{' '}
              <span className="font-semibold text-slate-700">CGE</span> e demais órgãos fiscalizadores.
            </p>
          </div>
        </div>
      </section>

      {/* Integrações Oficiais - Nova Seção */}
      <section id="integracoes" className="py-24 px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Badge className="mb-6 px-4 py-1.5 bg-blue-50 text-blue-700 border-blue-100 font-semibold">Integrações Oficiais</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 leading-tight">
              Conectando todo o{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">ciclo de conformidade</span>
            </h2>
            <p className="text-xl text-slate-600 font-light leading-relaxed">
              O BlueGov não substitui os sistemas já utilizados pela instituição.
              Ele conecta apontamentos, recomendações, evidências, responsáveis, prazos e planos de ação em um único ambiente, garantindo o acompanhamento completo das determinações dos órgãos de controle e a comprovação das providências adotadas.
            </p>
          </div>

          {/* Grid de Integrações */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* Gestão Documental */}
            <div className="group p-6 rounded-2xl bg-white border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Gestão Documental</h3>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full font-medium">SEI</span>
                <span className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full font-medium">ICP-Brasil</span>
              </div>
            </div>

            {/* Compras Públicas */}
            <div className="group p-6 rounded-2xl bg-white border border-slate-100 hover:border-emerald-200 hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Compras Públicas</h3>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-sm rounded-full font-medium">Compras.gov.br</span>
                <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-sm rounded-full font-medium">PNCP</span>
                <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-sm rounded-full font-medium">Sistemas Estaduais</span>
              </div>
            </div>

            {/* Contratos e Fiscalização */}
            <div className="group p-6 rounded-2xl bg-white border border-slate-100 hover:border-amber-200 hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <FileCheck className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Contratos e Fiscalização</h3>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-amber-50 text-amber-700 text-sm rounded-full font-medium">Contratos</span>
                <span className="px-3 py-1 bg-amber-50 text-amber-700 text-sm rounded-full font-medium">Aditivos</span>
                <span className="px-3 py-1 bg-amber-50 text-amber-700 text-sm rounded-full font-medium">Glosas</span>
              </div>
            </div>

            {/* Execução Orçamentária */}
            <div className="group p-6 rounded-2xl bg-white border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Execução Orçamentária e Financeira</h3>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full font-medium">SIAFI</span>
                <span className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full font-medium">SIGEF</span>
                <span className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full font-medium">AFM</span>
                <span className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full font-medium">Equivalentes Estaduais</span>
              </div>
            </div>

            {/* Planejamento Estratégico */}
            <div className="group p-6 rounded-2xl bg-white border border-slate-100 hover:border-cyan-200 hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Planejamento Estratégico</h3>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-cyan-50 text-cyan-700 text-sm rounded-full font-medium">PPA</span>
                <span className="px-3 py-1 bg-cyan-50 text-cyan-700 text-sm rounded-full font-medium">LDO</span>
                <span className="px-3 py-1 bg-cyan-50 text-cyan-700 text-sm rounded-full font-medium">LOA</span>
                <span className="px-3 py-1 bg-cyan-50 text-cyan-700 text-sm rounded-full font-medium">SIGPLAN</span>
              </div>
            </div>

            {/* Controle e Accountability */}
            <div className="group p-6 rounded-2xl bg-white border border-slate-100 hover:border-rose-200 hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Controle e Accountability</h3>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-rose-50 text-rose-700 text-sm rounded-full font-medium">TCE</span>
                <span className="px-3 py-1 bg-rose-50 text-rose-700 text-sm rounded-full font-medium">TCM</span>
                <span className="px-3 py-1 bg-rose-50 text-rose-700 text-sm rounded-full font-medium">CGE</span>
                <span className="px-3 py-1 bg-rose-50 text-rose-700 text-sm rounded-full font-medium">MP</span>
              </div>
            </div>
          </div>

          {/* Callout */}
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-center text-white mb-8">
            <p className="text-xl md:text-2xl font-medium leading-relaxed">
              &quot;Tudo o que o gestor precisa para responder auditorias com evidência, técnica e rastreabilidade — em um só lugar.&quot;
            </p>
          </div>

          {/* Selo de Conformidade */}
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-emerald-50 border border-emerald-200 rounded-full">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <span className="text-emerald-700 font-semibold text-sm">Aderente à Lei 14.133/2021 (Nova Lei de Licitações)</span>
            </div>
          </div>
        </div>
      </section>

      {/* Histórias de Adoção - anonimizadas */}
      <section id="casos" className="py-24 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Badge className="mb-6 px-4 py-1.5 bg-emerald-50 text-emerald-700 border-emerald-100 font-semibold">
              Aplicação Institucional
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Onde o BlueGov{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">gera valor</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed font-light">
              O BlueGov atende qualquer organização pública que precise acompanhar o cumprimento de obrigações, recomendações e ações de controle.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="onde-gera-valor-grid">
            {[
              {
                area: 'Controladorias',
                icon: Scale,
                desc: 'Centraliza apontamentos do Controle Interno, monitora o atendimento das recomendações e gera dossiês para a alta gestão.',
                examples: 'CGU · CGE · CGM',
              },
              {
                area: 'Prefeituras',
                icon: Building2,
                desc: 'Organiza determinações do TCE e do MPE, atribui responsáveis em cada secretaria e demonstra cumprimento perante a Câmara.',
                examples: 'Prefeituras de pequeno, médio e grande porte',
              },
              {
                area: 'Secretarias',
                icon: Layers,
                desc: 'Acompanha as obrigações específicas de cada pasta — finanças, saúde, educação — com painel próprio e responsáveis dedicados.',
                examples: 'Secretarias Estaduais e Municipais',
              },
              {
                area: 'Autarquias',
                icon: Shield,
                desc: 'Mantém em ordem o cumprimento de TACs, recomendações de auditorias federais e exigências da entidade supervisora.',
                examples: 'Agências reguladoras · Institutos · Fundações públicas',
              },
              {
                area: 'Empresas Públicas',
                icon: BarChart3,
                desc: 'Atende compliance corporativo, conselho fiscal, TCU e demandas internas de governança em uma única plataforma.',
                examples: 'Empresas de economia mista · Estatais',
              },
              {
                area: 'Governos Estaduais',
                icon: Flag,
                desc: 'Consolida a posição de conformidade de todas as secretarias e órgãos vinculados, com Visão Executiva para o Governador e a Casa Civil.',
                examples: 'Estados · Distrito Federal',
              },
            ].map((entity, i) => (
              <div
                key={i}
                className="group p-7 rounded-2xl border border-slate-200 bg-white hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-50/50 transition-all duration-500"
                data-testid={`onde-gera-valor-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <entity.icon className="w-6 h-6 text-emerald-700" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{entity.area}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">{entity.desc}</p>
                <p className="text-xs text-slate-400 italic border-t border-slate-100 pt-3">{entity.examples}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== NOVA SEÇÃO 5 — FAQ ============== */}
      <section id="faq" className="py-24 px-8 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <Badge className="mb-6 px-4 py-1.5 bg-amber-50 text-amber-700 border-amber-100 font-semibold">
              Perguntas Frequentes
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Dúvidas mais comuns
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed font-light">
              Esclarecemos o que o BlueGov é — e o que ele não é.
            </p>
          </div>

          <div className="space-y-4" data-testid="faq-section">
            {[
              {
                q: 'O BlueGov substitui o SEI?',
                a: 'Não. O SEI administra documentos e processos. O BlueGov administra o cumprimento das determinações, recomendações, planos de ação e evidências decorrentes desses processos. São camadas complementares.',
              },
              {
                q: 'O BlueGov substitui o ERP?',
                a: 'Não. Ele complementa os sistemas existentes, atuando na camada de governança, conformidade e acompanhamento. O ERP segue controlando recursos; o BlueGov controla a execução de ações corretivas.',
              },
              {
                q: 'O BlueGov substitui o Portal da Transparência?',
                a: 'Não. O Portal publica informações. O BlueGov controla o atendimento aos apontamentos que essas informações geram. Pode, opcionalmente, publicar automaticamente evidências no portal do órgão.',
              },
              {
                q: 'O BlueGov funciona sem integrações?',
                a: 'Sim. Órgãos menores podem operar integralmente dentro do BlueGov — atos, evidências, planos de ação, prazos, responsáveis e dossiês são gerenciados nativamente.',
              },
              {
                q: 'O BlueGov funciona com integrações?',
                a: 'Sim. Pode consumir informações de sistemas já existentes (SEI, Azure AD, ICP-Brasil, sistemas corporativos via API) e transformá-las em ações controladas e auditáveis.',
              },
              {
                q: 'O BlueGov substitui o controle interno do órgão?',
                a: 'Não. Ele dá ao controle interno a plataforma que faltava — para registrar achados, designar responsáveis, acompanhar planos de ação e gerar dossiês defensáveis em auditorias externas.',
              },
              {
                q: 'Quanto tempo leva para implantar?',
                a: 'Para órgãos sem integração: poucos dias. Para órgãos com integração de SEI e SSO corporativo: até 6 semanas, incluindo configuração e treinamento.',
              },
              {
                q: 'Os dados ficam protegidos?',
                a: 'Sim. Multi-tenant isolado por design, criptografia em repouso (AES-256), TLS 1.3, hash chain SHA-256 imutável, conformidade LGPD nativa e auditoria de todos os acessos.',
              },
            ].map((item, i) => (
              <details
                key={i}
                className="group p-6 rounded-xl border border-slate-200 bg-white hover:border-blue-300 transition-all"
                data-testid={`faq-item-${i}`}
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="text-base md:text-lg font-bold text-slate-900 pr-4">{item.q}</h3>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <p className="mt-4 text-base text-slate-600 leading-relaxed font-light">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Premium */}
      <section id="contato" className="py-24 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20">
            <div>
              <Badge className="mb-6 px-4 py-1.5 bg-blue-50 text-blue-700 border-blue-100 font-semibold">Próximo Passo</Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 leading-tight">
                Conheça o BlueGov{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">na prática</span>
              </h2>
              <p className="text-xl text-slate-600 mb-12 leading-relaxed font-light">
                Agende uma apresentação personalizada e descubra como transformar 
                a gestão de atos de controle no seu órgão.
              </p>
              
              <div className="space-y-8">
                {[
                  { icon: Play, title: 'Demo Personalizada', desc: 'Veja a plataforma funcionando com casos reais do seu contexto' },
                  { icon: Users, title: 'Especialistas Dedicados', desc: 'Equipe com experiência comprovada em gestão pública' },
                  { icon: Lock, title: 'Dados 100% Protegidos', desc: 'Conformidade total com LGPD e padrões internacionais' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-5">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-bold text-lg text-slate-900 mb-1">{item.title}</p>
                      <p className="text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Premium Contact Form */}
            <Card className="border-0 shadow-2xl shadow-slate-200/50 rounded-3xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-700 p-6">
                <h3 className="text-xl font-bold text-white">Solicitar Apresentação</h3>
                <p className="text-blue-200 text-sm">Preencha o formulário e retornaremos em até 24h</p>
              </div>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-slate-700 font-semibold">Nome</Label>
                      <Input 
                        id="name" 
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Seu nome completo"
                        required
                        className="mt-2 h-12 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="role" className="text-slate-700 font-semibold">Cargo</Label>
                      <Input 
                        id="role"
                        value={formData.role}
                        onChange={(e) => setFormData({...formData, role: e.target.value})}
                        placeholder="Ex: Secretário"
                        required
                        className="mt-2 h-12 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="organization" className="text-slate-700 font-semibold">Órgão / Instituição</Label>
                    <Input 
                      id="organization"
                      value={formData.organization}
                      onChange={(e) => setFormData({...formData, organization: e.target.value})}
                      placeholder="Ex: Secretaria de Fazenda do Estado de SP"
                      required
                      className="mt-2 h-12 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" className="text-slate-700 font-semibold">E-mail</Label>
                      <Input 
                        id="email" 
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="seu@email.gov.br"
                        required
                        className="mt-2 h-12 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-slate-700 font-semibold">Telefone</Label>
                      <Input 
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="(00) 00000-0000"
                        className="mt-2 h-12 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="message" className="text-slate-700 font-semibold">Mensagem (opcional)</Label>
                    <Textarea 
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Conte-nos sobre suas necessidades..."
                      rows={4}
                      className="mt-2 rounded-xl resize-none border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-blue-700 font-bold text-base rounded-xl shadow-lg shadow-blue-500/25"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>Enviando...</>
                    ) : (
                      <>
                        Solicitar Apresentação
                        <Send className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ============== SEÇÃO DE FECHAMENTO INSTITUCIONAL ============== */}
      <section id="visao-institucional" className="py-24 px-8 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 relative overflow-hidden" data-testid="visao-institucional">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:3rem_3rem]" />
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl" />

        <div className="max-w-5xl mx-auto relative z-10 text-center">
          <Badge className="mb-8 px-4 py-1.5 bg-white/10 text-blue-200 border-white/20 font-semibold backdrop-blur-sm" data-testid="visao-institucional-badge">
            Visão Institucional
          </Badge>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-[1.15]">
            Sua instituição já possui sistemas.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-cyan-300 to-blue-200">
              Mas quem controla o cumprimento das recomendações?
            </span>
          </h2>

          <div className="max-w-3xl mx-auto space-y-5 mb-12">
            <p className="text-lg md:text-xl text-blue-100/90 leading-relaxed font-light">
              O BlueGov transforma <strong className="text-white">apontamentos</strong>,{' '}
              <strong className="text-white">recomendações</strong> e{' '}
              <strong className="text-white">determinações</strong> em ações{' '}
              <strong className="text-white">acompanhadas</strong>,{' '}
              <strong className="text-white">rastreáveis</strong> e{' '}
              <strong className="text-white">comprováveis</strong>.
            </p>
            <p className="text-lg md:text-xl text-blue-100/85 leading-relaxed font-light">
              Porque identificar um problema é apenas o começo.
            </p>
            <p className="text-xl md:text-2xl text-white font-semibold leading-snug">
              Demonstrar que ele foi tratado é o que protege a instituição.
            </p>
          </div>

          {/* Bullets institucionais */}
          <div className="max-w-3xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3 mb-12" data-testid="visao-institucional-bullets">
            {[
              'Centraliza apontamentos',
              'Define responsáveis',
              'Controla prazos',
              'Recebe evidências',
              'Mede conformidade',
              'Gera dossiês',
              'Demonstra resultados',
              'Protege a gestão',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm md:text-base text-white/90 font-medium justify-center md:justify-start">
                <CheckCircle2 className="w-4 h-4 text-cyan-300 flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <Button
            size="lg"
            data-testid="visao-institucional-cta"
            className="bg-white text-blue-900 hover:bg-blue-50 text-base h-14 px-10 shadow-2xl font-semibold rounded-xl"
            onClick={() => document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Agendar Apresentação Institucional
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Premium Footer */}
      <footer className="bg-gradient-to-b from-slate-900 to-slate-950 py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-16 mb-16">
            <div className="md:col-span-2">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-white">BlueGov</span>
                  <Badge className="text-[10px] bg-blue-500/20 text-blue-300 border-blue-500/30 font-semibold">AI</Badge>
                </div>
              </div>
              <p className="text-slate-400 max-w-md leading-relaxed text-lg">
                Plataforma de Inteligência Artificial para gestão de atos de controle. 
                Governança, conformidade e proteção para o Estado moderno.
              </p>
            </div>
            
            <div>
              <p className="text-white font-bold text-lg mb-6">Solução</p>
              <ul className="space-y-3">
                {['Gestão de Atos', 'IA & Classificação', 'Evidências', 'Dashboards', 'Conformidade'].map((item, i) => (
                  <li key={i}>
                    <a href="#" className="text-slate-400 hover:text-white transition-colors">{item}</a>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <p className="text-white font-bold text-lg mb-6">Contato</p>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-slate-400">
                  <Mail className="w-5 h-5" />
                  contato@bluegov.ai
                </li>
                <li className="flex items-center gap-3 text-slate-400">
                  <Phone className="w-5 h-5" />
                  (11) 0000-0000
                </li>
                <li className="flex items-center gap-3 text-slate-400">
                  <MapPin className="w-5 h-5" />
                  São Paulo, Brasil
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-10 flex flex-col md:flex-row justify-between items-center gap-6" data-testid="footer-legal-links">
            <p className="text-slate-500">
              © {new Date().getFullYear()} BlueGov. Todos os direitos reservados.
            </p>
            <div className="flex flex-wrap gap-6 md:gap-8 justify-center">
              <a href="/termos-de-uso" className="text-slate-500 hover:text-white transition-colors" data-testid="footer-link-termos">Termos de Uso</a>
              <a href="/politica-de-privacidade" className="text-slate-500 hover:text-white transition-colors" data-testid="footer-link-privacidade">Política de Privacidade</a>
              <a href="/lgpd" className="text-slate-500 hover:text-white transition-colors" data-testid="footer-link-lgpd">LGPD</a>
              <a href="/cookies" className="text-slate-500 hover:text-white transition-colors" data-testid="footer-link-cookies">Cookies</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
